import LenderLogo from './LenderLogo'
import { useRates, type RateRow } from '../hooks/useRates'
import { tenantConfig } from '../config/tenant'

function dedupe(rates: RateRow[]): string[] {
  const seen = new Set<string>()
  const names: string[] = []
  for (const r of rates) {
    if (!seen.has(r.lender_name)) {
      seen.add(r.lender_name)
      names.push(r.lender_name)
    }
  }
  return names
}

/**
 * "We compare N+ lenders" credibility band. The headline count reflects the full
 * panel; the logo marquee shows only Tier 1 & 2 lenders (curated in /dataentry) so
 * the public wall carries recognisable brands, not obscure white-labels. Falls
 * back to all lenders if no tiers are set yet.
 */
export default function LenderWall() {
  const { data } = useRates()
  const rates = data?.rates ?? []
  const tiered = dedupe(rates.filter((r) => r.tier === 1 || r.tier === 2))
  const lenders = tiered.length ? tiered : dedupe(rates)

  if (lenders.length === 0) return null

  // Fixed-width, centred box per logo — keeps the marquee track a stable width as
  // logo images load async (variable-width items make the seamless loop skew/jank).
  const Logo = ({ name }: { name: string }) => (
    <span className="inline-flex items-center justify-center w-36 shrink-0 opacity-70 hover:opacity-100 transition-opacity">
      <LenderLogo name={name} size={30} />
    </span>
  )

  return (
    <section className="py-14 border-y border-[#E8E8E2] bg-[#FAFAF7]" data-testid="lender-wall">
      <div className="max-w-7xl mx-auto px-6 text-center mb-8">
        <div className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted mb-1">Our lender panel</div>
        <h2 className="font-heading text-2xl md:text-3xl text-ink">
          We compare {tenantConfig.lenderCount}+ lenders so you don&apos;t have to.
        </h2>
      </div>
      <div className="marquee" style={{ ['--marquee-duration' as string]: `${Math.max(30, lenders.length * 1.8)}s` }}>
        <div className="marquee-track items-center">
          {lenders.map((n, i) => <Logo key={`a-${i}`} name={n} />)}
          {lenders.map((n, i) => <Logo key={`b-${i}`} name={n} />)}
        </div>
      </div>
    </section>
  )
}
